import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { EmptyContent } from 'src/components/empty-content';
import AddressField from '../../components/AddressField';
import XCurrencyField from '../../components/XCurrencyField';
import XDateField from '../../components/XDateField';
import {
  DexCurrentOrderRow,
  HyperliquidFrontendOpenOrder,
  SelectedWallet,
  fetchHyperliquidWalletOrders,
  toNumber,
} from '../../services/hyperliquidUserService';

const getSide = (side?: string) => {
  if (side === 'B') return { label: 'Buy', color: 'success' as const };
  if (side === 'A') return { label: 'Sell', color: 'error' as const };
  return { label: '-', color: 'default' as const };
};

const getOrderFlags = (order: HyperliquidFrontendOpenOrder) => {
  const flags: string[] = [];
  if (order.reduceOnly) flags.push('Reduce only');
  if (order.isTrigger) flags.push('Trigger');
  if (order.isPositionTpsl) flags.push('TP/SL');
  return flags;
};

const DexCurrentOrders = ({ selectedWallet }: { selectedWallet: SelectedWallet[] }) => {
  const t = useTranslate();
  const [orders, setOrders] = useState<DexCurrentOrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const walletAddresses = useMemo(
    () => Array.from(new Set((selectedWallet ?? []).map((wallet) => wallet.walletAddress).filter(Boolean))),
    [selectedWallet]
  );

  useEffect(() => {
    let active = true;

    if (walletAddresses.length === 0) {
      setOrders([]);
      setHasError(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setHasError(false);

    Promise.allSettled(walletAddresses.map((walletAddress) => fetchHyperliquidWalletOrders(walletAddress)))
      .then((results) => {
        if (!active) return;

        const nextOrders = results.flatMap((result) =>
          result.status === 'fulfilled' ? result.value : []
        );

        const failedCount = results.filter((result) => result.status === 'rejected').length;

        setOrders(nextOrders);
        setHasError(failedCount > 0 && nextOrders.length === 0);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [walletAddresses]);

  if (loading) {
    return <Skeleton height={320} width="100%" />;
  }

  if (hasError) {
    return (
      <EmptyContent
        title={t('common.noData', { _: 'No data' })}
        description={t('module.userInfo.dexCurrentOrders.fetchFailed', {
          _: 'Failed to load current orders from Hyperliquid',
        })}
      />
    );
  }

  if (walletAddresses.length === 0 || orders.length === 0) {
    return (
      <EmptyContent
        title={t('common.noData', { _: 'No data' })}
        description={t('module.userInfo.dexCurrentOrders.empty', {
          _: 'No open orders for the selected DEX wallets',
        })}
      />
    );
  }

  return (
    <TableContainer>
      <Table
        sx={{
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('module.userInfo.wallet.address')}</TableCell>
            <TableCell>{t('module.userInfo.coin')}</TableCell>
            <TableCell>{t('module.userInfo.side', { _: 'Side' })}</TableCell>
            <TableCell>{t('module.userInfo.size')}</TableCell>
            <TableCell>{t('module.userInfo.dexCurrentOrders.remainingSize', { _: 'Remaining Size' })}</TableCell>
            <TableCell>{t('module.userInfo.price')}</TableCell>
            <TableCell>{t('module.userInfo.orderType', { _: 'Order type' })}</TableCell>
            <TableCell>{t('module.userInfo.dexCurrentOrders.triggerPrice', { _: 'Trigger Price' })}</TableCell>
            <TableCell>{t('module.userInfo.dexCurrentOrders.flags', { _: 'Flags' })}</TableCell>
            <TableCell>{t('module.userInfo.time')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => {
            const side = getSide(order.side);
            const flags = getOrderFlags(order);

            return (
              <TableRow key={order.id} hover>
                <TableCell>
                  <AddressField address={order.walletAddress} sx={{ width: '180px' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {order.coin || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={side.label} color={side.color} size="small" />
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={toNumber(order.origSz)}
                    currency={{ symbol: `(${order.coin ?? ''})`, position: 'after', space: true }}
                  />
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={toNumber(order.sz)}
                    currency={{ symbol: `(${order.coin ?? ''})`, position: 'after', space: true }}
                  />
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={toNumber(order.limitPx)}
                    currency={{ symbol: '$', position: 'before' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.orderType || '-'}</Typography>
                </TableCell>
                <TableCell>
                  {order.isTrigger ? (
                    <XCurrencyField
                      value={toNumber(order.triggerPx)}
                      currency={{ symbol: '$', position: 'before' }}
                    />
                  ) : (
                    <Box sx={{ px: 1 }}>
                      <Typography variant="body2">-</Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {flags.length > 0 ? (
                      flags.map((flag) => <Chip key={flag} label={flag} size="small" variant="outlined" />)
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {order.timestamp ? <XDateField relative value={new Date(order.timestamp).toISOString()} /> : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DexCurrentOrders;
